import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getSpecialDays, deleteSpecialDay, createSpecialDay, updateSpecialDay } from "../../../utils/api";

const columns = [
  { key: "title", label: "Title" },
  { 
    key: "date", 
    label: "Date",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
  { key: "category", label: "Category" },
  { 
    key: "nationalDay", 
    label: "National Day",
    render: (value) => value ? "Yes" : "No"
  },
  {
    key: "articleFileName",
    label: "Article File",
    render: (value) => value || "-"
  },
  {
    key: "mediaItems",
    label: "Media Count",
    render: (value) => Array.isArray(value) ? value.length : 0
  },
  { key: "status", label: "Status" },
];

const filters = [
  {
    key: "category",
    label: "Filter by Category",
    type: "select",
    options: [
      { value: "Religious", label: "Religious" },
      { value: "Cultural", label: "Cultural" },
      { value: "National", label: "National" },
      { value: "Festival", label: "Festival" },
    ]
  },
  {
    key: "status",
    label: "Filter by Status",
    type: "select",
    options: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ]
  },
];

const initialFormData = {
  title: "",
  description: "",
  articleContent: "",
  date: "",
  category: "Religious",
  nationalDay: false,
  status: "active",
  year: new Date().getFullYear()
};

export default function SpecialDays() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchData = () => getSpecialDays(selectedYear).then(res => res.specialDays || []);

  const handleAdd = () => {
    setFormData({
      ...initialFormData,
      date: "",
      year: selectedYear
    });
    setIsEditMode(false);
    setSelectedDay(null);
    setModalOpen(true);
  };

  const handleEdit = (day) => {
    setSelectedDay(day);
    setFormData({
      title: day.title || "",
      description: day.description || "",
      articleContent: day.articleContent || "",
      date: day.date ? new Date(day.date).toISOString().split('T')[0] : "",
      category: day.category || "Religious",
      nationalDay: day.nationalDay || false,
      status: day.status || "active",
      year: day.year || selectedYear
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (day) => {
    handleEdit(day);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditMode && selectedDay) {
        await updateSpecialDay(selectedDay._id, formData);
        alert("Special Day updated successfully!");
      } else {
        await createSpecialDay(formData);
        alert("Special Day created successfully!");
      }
      setModalOpen(false);
    } catch (error) {
      alert(error.message || "Failed to save special day");
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
          <label><strong>Filter by Year:</strong></label>
          <select 
            value={selectedYear} 
            onChange={handleYearChange}
            style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
          >
            {Array.from({ length: 10 }, (_, i) => {
              const year = new Date().getFullYear() - 5 + i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>
        </div>
        <AdminList
          title="Special Days"
          fetchData={fetchData}
          deleteData={deleteSpecialDay}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search special days..."
        />
      </main>

      {/* Create/Edit Special Day Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Special Day" : "Add New Special Day"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                required
                placeholder="Enter title"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                rows="3"
                placeholder="Enter description"
              />
            </div>
            <div className="form-group">
              <label>Article Content</label>
              <textarea
                name="articleContent"
                value={formData.articleContent}
                onChange={handleFormChange}
                rows="5"
                placeholder="Optional article text shown in app details"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Date & Category</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Year</label>
                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleFormChange}
                  min="1900"
                  max="2100"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                >
                  <option value="Religious">Religious</option>
                  <option value="Cultural">Cultural</option>
                  <option value="National">National</option>
                  <option value="Festival">Festival</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  name="nationalDay"
                  checked={formData.nationalDay}
                  onChange={handleFormChange}
                />
                National Day
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (isEditMode ? "Update Special Day" : "Create Special Day")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
